import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CardsSlider } from "../../../../components/CardsSlider";
import { CardsContext } from "../../CardsContext";
import { AppContext } from '../../../../AppContext';
import { useURLQuery } from '../../hooks/useURLQuery';
import { SectionHead } from '../../../../components/SectionHead/SectionHead';
import { Card } from '../../../../components/Card';
import { sortByAccess, sortByDate } from "../../utils/utils";
import { sendPageViewEvent } from '../../../../utils/utils';
import './SearchPage.scss';

function cardCheck(card, searchQuery, coaches) {

    if (card?.hide_from_listing) return false;

    const isTitlePassed = card.title?.toLowerCase().includes(searchQuery);
    const isCoachPassed = card.coach?.some((coachId) => coaches.get(coachId)?.title.toLowerCase().includes(searchQuery));
    const isAccessPassed = card.access?.toLowerCase().includes(searchQuery);
    const isLevelPassed = card.meta_level?.some((level) => level.toLowerCase().includes(searchQuery));

    return isTitlePassed || isCoachPassed || isAccessPassed || isLevelPassed;
}

export function SearchPage({ onToggleWatchLater }) {
  const { target, isTargetClasses, cards, cardsConfig } = useContext(CardsContext);
  const { coaches } = useContext(AppContext);
  const urlQuery = useURLQuery();
  const searchQuery = urlQuery.get('query');
  const [current, setCurrent] = useState(1);
  const sendGAPageEventTimeoutId = useRef(null);

  const searchedCards = useMemo(() => {
    if(!cards) {
      return null;
    }

    if(!searchQuery) {
      return cards;
    }

    setCurrent(1);

    const searchQueryLowerCase = searchQuery.toLowerCase();

    return cards.filter((card) => {
      const isCardPassed = cardCheck(card, searchQueryLowerCase, coaches);
      const isSeriesPassed = card?.series?.length
        ? card.series.some((card) => cardCheck(card, searchQueryLowerCase, coaches))
        : false;

      return isCardPassed || isSeriesPassed;
    });
  }, [cards, coaches, searchQuery]);

  useEffect(() => {
    clearTimeout(sendGAPageEventTimeoutId.current);

    sendGAPageEventTimeoutId.current = setTimeout(() => {
      if(!searchQuery) {
        return;
      }

      sendPageViewEvent(`Search - ${searchQuery}`);
    }, 1500);

    return () => {
      clearTimeout(sendGAPageEventTimeoutId.current);
    };
  }, [searchQuery]);

  if(!searchedCards?.length) {
    return <p className="no-data">No results</p>;
  }

  const seriesCards = searchedCards.filter((card) => card?.series?.length).sort((a, b) => sortByDate(a.date, b.date)).sort((a, b) => sortByAccess(a.access, b.access));
  const noSeriesCards = searchedCards.filter((card) => !card?.series?.length).slice(0, current * 10).sort((a, b) => sortByDate(a.date, b.date)).sort((a, b) => sortByAccess(a.access, b.access));

  return (
    <div>
      <SectionHead
        title="Search Results"
        count={searchedCards.length}
      />

      {!!seriesCards.length && (
        <CardsSlider
          addPopupSeriesSearchParam
          isShowDate={isTargetClasses}
          isShowWatchLater={cardsConfig.watchLater}
          type={target}
          cards={seriesCards}
          className="SearchPage__series"
        />
      )}

      {!!noSeriesCards.length && (
        <InfiniteScroll
          dataLength={noSeriesCards.length}
          next={() => setCurrent((prevCurrent) => prevCurrent + 1)}
          hasMore={noSeriesCards.length < searchedCards.length - seriesCards.length}
          loader={<h4>Loading...</h4>}
          endMessage={null}
        >
          {noSeriesCards.map((cardItem) => (
            <Card
              isRow
              isShowWatchLater={cardsConfig.watchLater}
              addPopupSeriesSearchParam
              type={target}
              cardItem={cardItem}
              key={cardItem.id}
              onToggleWatchLater={onToggleWatchLater}
            />
          ))}
        </InfiniteScroll>
      )}
    </div>
  );
}
